import {useState, useCallback, useMemo} from 'react';

type UseTokenAmountInputParams = {
    initial?: number | null;
    decimals: number;
    balance?: number; // Optional max limit
    allowEmpty?: boolean;
    minValue?: number;
}

export function usePreciseDecimalInput({
                                           initial = null,
                                           decimals,
                                           balance = Number.MAX_SAFE_INTEGER,
                                           allowEmpty = true,
                                           minValue = 0,
                                       }: UseTokenAmountInputParams) {
    const [value, setValue] = useState<number | null>(initial);
    const [display, setDisplay] = useState<string>(initial?.toString() ?? '0');
    const [isOutOfRange, setOutOfRange] = useState(false);

    const isTmp = (str: string): boolean =>
        !!str && (str.endsWith(".") || /^\d+\.$/.test(str));

    const regex = useMemo(() => {
        return new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`);
    }, [decimals]);

    const onChange = useCallback(
        (raw: string) => {

            const isTmpValue = isTmp(raw);

            if (regex.test(raw)) {
                setDisplay(raw);

                const numeric = Number(raw);
                const validNumber = !isNaN(numeric);

                if (validNumber && !isTmpValue) {
                    if (numeric >= minValue && numeric <= balance) {
                        setOutOfRange(false);
                        setValue(numeric);
                    } else {
                        setOutOfRange(true);
                        setValue(null); // 👈 но не сбрасывай display!
                    }
                } else if (isTmpValue) {
                    setOutOfRange(false);
                } else {
                    setValue(null);
                    setOutOfRange(false);
                }
            }
        },
        [decimals, allowEmpty, balance, minValue]
    );

    const setRaw = (raw: string) => {
        const numeric = Number(raw);
        setDisplay(raw);

        if (!isNaN(numeric)) {
            setValue(numeric);
            const tooSmall = numeric < minValue;
            const tooLarge = numeric > balance;
            setOutOfRange(tooSmall || tooLarge);
        } else {
            setValue(null);
            setOutOfRange(false);
        }
    };


    const reset = () => {
        setValue(null);
        setDisplay('');
        setOutOfRange(false);
    };

    return {
        value, // number | null
        display, // string
        isTmpValue: display.endsWith('.') || /^\d+\.$/.test(display),
        isOutOfRange, // boolean
        onChange,
        setValue,
        reset,
        setRaw,
    };
}
